import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send, MessageCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/layout/Breadcrumbs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        lead_type: 'contact',
        source: 'contact_page',
        language
      });
      toast.success(language === 'nl' ? 'Bericht succesvol verzonden!' : 'Message sent successfully!');
      setFormData({ name: '', email: '', phone: '', website: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(language === 'nl' ? 'Er ging iets mis. Probeer het opnieuw.' : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEOHead
        title={language === 'nl' ? 'Contact | Plan een vrijblijvende kennismaking' : 'Contact | Book a free introduction about Dutch SEO'}
        description={language === 'nl'
          ? 'Neem contact op voor een vrijblijvende kennismaking. Bespreek je ambities en ontdek hoe een holistische SEO-aanpak jouw groei versnelt.'
          : 'Ready to discuss your Dutch market ambitions? Book a free 30-minute call and discover how a native Dutch SEO specialist can accelerate your growth.'}
        canonical="https://www.kesult.com/contact"
        lang={language}
      />
      {/* Hero */}
      <section className="relative bg-[#0B1A2E] text-white overflow-hidden" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-32 lg:pb-40 relative z-10">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-5">
              Contact
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-tight mb-6" data-testid="contact-title">
              {language === 'nl' ? "Let's Talk About" : "Let's Talk About"}{' '}
              <span className="text-[#4286F4]">{language === 'nl' ? 'Organic Growth' : 'Organic Growth in the Netherlands'}</span>
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl">
              {language === 'nl'
                ? 'Benieuwd wat SEO voor jouw bedrijf kan betekenen? Neem vrijblijvend contact op en ik denk graag met je mee.'
                : 'Curious what SEO can do for your business? Get in touch and I\'d be happy to think along.'}
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-white" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </section>

      <Breadcrumbs />

      {/* Contact Content */}
      <section className="py-20 lg:py-28 bg-white" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
                {language === 'nl' ? 'Contactgegevens' : 'Contact details'}
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-8">
                {language === 'nl' ? 'Neem direct contact op' : 'Get in touch directly'}
              </h2>

              <div className="space-y-5">
                <a
                  href="mailto:info@kesult.com"
                  className="flex items-center gap-4 p-4 rounded-2xl border border-gray-100 hover:border-[#4286F4]/30 hover:shadow-md transition-all group"
                  data-testid="contact-email-link"
                >
                  <div className="w-12 h-12 bg-[#F1F0EF] rounded-xl flex items-center justify-center group-hover:bg-[#4286F4] transition-colors">
                    <Mail className="h-5 w-5 text-[#4286F4] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-xs text-[#999] uppercase tracking-wider font-semibold">Email</p>
                    <p className="text-[#08080A] font-semibold">info@kesult.com</p>
                  </div>
                </a>

                <a
                  href="tel:+31612614620"
                  className="flex items-center gap-4 p-4 rounded-2xl border border-gray-100 hover:border-[#4286F4]/30 hover:shadow-md transition-all group"
                  data-testid="contact-phone-link"
                >
                  <div className="w-12 h-12 bg-[#F1F0EF] rounded-xl flex items-center justify-center group-hover:bg-[#4286F4] transition-colors">
                    <Phone className="h-5 w-5 text-[#4286F4] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-xs text-[#999] uppercase tracking-wider font-semibold">{language === 'nl' ? 'Telefoon' : 'Phone'}</p>
                    <p className="text-[#08080A] font-semibold">+31 6 1261 4620</p>
                  </div>
                </a>

                <a
                  href="https://wa.me/31612614620"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 rounded-2xl border border-gray-100 hover:border-[#25D366]/30 hover:shadow-md transition-all group"
                  data-testid="contact-whatsapp-link"
                >
                  <div className="w-12 h-12 bg-[#25D366]/10 rounded-xl flex items-center justify-center group-hover:bg-[#25D366] transition-colors">
                    <MessageCircle className="h-5 w-5 text-[#25D366] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-xs text-[#999] uppercase tracking-wider font-semibold">WhatsApp</p>
                    <p className="text-[#08080A] font-semibold">+31 6 1261 4620</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 rounded-2xl border border-gray-100">
                  <div className="w-12 h-12 bg-[#F1F0EF] rounded-xl flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-[#4286F4]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#999] uppercase tracking-wider font-semibold">{language === 'nl' ? 'Locatie' : 'Location'}</p>
                    <p className="text-[#08080A] font-semibold">Veenendaal, Nederland</p>
                  </div>
                </div>
              </div>

              {/* Business Info */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm text-[#999]">
                  <span>Kesult</span>
                  <span>{language === 'nl' ? 'Eenmanszaak' : 'Sole proprietorship'}</span>
                  <span>KVK 42002486</span>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <p className="text-sm text-[#999] mb-6 uppercase tracking-wider font-semibold">
                {language === 'nl' ? 'Stuur een bericht' : 'Send a message'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-1.5">
                      {language === 'nl' ? 'Naam' : 'Name'} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl text-sm focus:border-[#4286F4] focus:outline-none transition-colors"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-1.5">
                      {language === 'nl' ? 'Telefoon' : 'Phone'}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl text-sm focus:border-[#4286F4] focus:outline-none transition-colors"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-1.5">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl text-sm focus:border-[#4286F4] focus:outline-none transition-colors"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333] mb-1.5">
                      Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      placeholder="https://"
                      value={formData.website}
                      onChange={handleChange}
                      className="w-full h-12 px-4 border border-gray-200 rounded-xl text-sm focus:border-[#4286F4] focus:outline-none transition-colors"
                      data-testid="contact-website-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#333] mb-1.5">
                    {language === 'nl' ? 'Bericht' : 'Message'} *
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:border-[#4286F4] focus:outline-none transition-colors resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#4286F4] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#3070d4] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="contact-submit-btn"
                >
                  {loading
                    ? (language === 'nl' ? 'Verzenden...' : 'Sending...')
                    : (language === 'nl' ? 'Verstuur' : 'Send')
                  }
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#4286F4] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="text-2xl sm:text-3xl font-bold">
            {language === 'nl' ? 'Liever direct bellen of appen?' : 'Prefer calling or texting?'}
          </h2>
          <div className="flex gap-4">
            <a href="tel:+31612614620">
              <button className="bg-white text-[#4286F4] font-semibold px-8 py-3.5 rounded-full hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
                {language === 'nl' ? 'Bel me' : 'Call me'}
                <Phone className="h-4 w-4" />
              </button>
            </a>
            <a href="https://wa.me/31612614620" target="_blank" rel="noopener noreferrer">
              <button className="border-2 border-white/30 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center gap-2">
                WhatsApp
                <MessageCircle className="h-4 w-4" />
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
